// connection statuses (driven by RTCPeerConnection.connectionState + closedGracefullyRef).
// connecting: before connected, until pc.connectionState === 'connected'
// connected: showing tracks and playing
// reconnecting: pc.connectionState === 'disconnected' (recoverable)
// disconnected: unrecoverable (failed, or closed without graceful close, or signaling errors)
// connection_ended: connection was closed gracefully
export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'connection_ended';

export const POLLING_INTERVAL_MS = 500;
export const MAX_VIDEO_FEEDS = 4;
export const LIVE_STREAM_NS = 'content.fleet.details.general_details.live_stream';
/** Camera name translations (content.cameras.<name>). */
export const CAMERAS_NS = 'content.cameras';

/** Device track names that have translation keys under content.cameras. */
export const TRANSLATABLE_CAMERA_NAMES = new Set<string>([
    'InCabinCamera',
    'FrontFacingCamera',
    'BlindSpotCamera',
    'RightBlindSpotCamera',
    'LeftBlindSpotCamera',
    'RearCamera',
]);

/**
 * Parse answer SDP for device track names: extract a=mid and a=msid per media section.
 * Returns map of mid (string) -> track name (e.g. "InCabinCamera", "FrontFacingCamera").
 */
export const parseTrackNamesFromSdp = (sdp: string): Record<string, string> => {
    const map: Record<string, string> = {};
    const lines = sdp.split(/\r?\n/);
    let currentMid: string | null = null;
    for (const line of lines) {
        if (line.startsWith('a=mid:')) {
            currentMid = line.slice(6).trim();
        } else if (line.startsWith('a=msid:') && currentMid !== null) {
            const rest = line.slice(7).trim();
            const tokens = rest.split(/\s+/);
            if (tokens.length >= 2) {
                map[currentMid] = tokens[1];
            } else if (tokens.length === 1 && tokens[0] !== '-') {
                map[currentMid] = tokens[0];
            }
        }
    }
    return map;
};

export const getErrorMessage = (error: unknown): string => {
    const apiError = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
    if (apiError) return apiError;
    const message = (error as { message?: string })?.message;
    return message ?? '';
};

/** Wrapped data channel message from device. Supports multiple message types via `type`. */
export type DataChannelMessage =
    | { type: 'track_names'; map: Record<string, string> }
    | { type: string; [key: string]: unknown };

export const isMidToTrackNameMap = (obj: Record<string, unknown>): obj is Record<string, string> => {
    const entries = Object.entries(obj);
    if (entries.length === 0) return false;
    return entries.every(([, v]) => typeof v === 'string');
};

/**
 * Normalizes RTCDataChannel message data to string. Android sends binary (ArrayBuffer);
 * the browser may receive either string or ArrayBuffer depending on how the peer sent it.
 */
export const dataChannelPayloadToString = (data: string | ArrayBuffer | Blob): string => {
    if (typeof data === 'string') return data;
    if (data instanceof ArrayBuffer) return new TextDecoder().decode(new Uint8Array(data));
    // Blob: sync path not available; caller would need async. Assume we don't get Blob from Android.
    return '';
};

/**
 * Parses a wrapped data channel message and dispatches by type. Returns true if the message
 * was recognized and handled. Supports: type 'track_names' with a `map` (mid -> track_name).
 */
export const handleDataChannelMessage = (
    data: string,
    setTrackNameByMid: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): boolean => {
    try {
        const parsed = JSON.parse(data) as unknown;
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return false;
        }
        const obj = parsed as DataChannelMessage & Record<string, unknown>;

        const msgType = obj.type;
        if (typeof msgType === 'string') {
            switch (msgType) {
                case 'track_names': {
                    const map = obj.map;
                    if (
                        map !== null &&
                        typeof map === 'object' &&
                        !Array.isArray(map) &&
                        isMidToTrackNameMap(map as Record<string, unknown>)
                    ) {
                        setTrackNameByMid(map as Record<string, string>);
                        return true;
                    }
                    return false;
                }
                default:
                    // Unknown type; could log or extend later
                    return false;
            }
        }
    } catch {
        return false;
    }

    return false;
};

export interface VideoFeedSlot {
    stream: MediaStream;
    trackId: string;
}

/** Credentials for device or browser: shared username + per-server passwords. */
export interface IceCredentials {
    username: string;
    servers: Record<string, string>;
}

/** Parsed live stream command payload from backend. */
export interface LiveStreamCommandPayload {
    session_id: number;
    expiration: number;
    device?: IceCredentials;
    browser?: IceCredentials;
}

/**
 * Parse browser credentials from live stream command payload into RTCIceServer configs.
 * For each key in browser.servers: url = <key>.openeyes.com, username = browser.username, credential = value.
 * Returns one RTCIceServer per key with urls: ['stun:<url>:3478', 'turn:<url>:3478'].
 */
export const parseIceServersFromCommandPayload = (payload: LiveStreamCommandPayload): RTCIceServer[] => {
    const browser = payload.browser;
    if (!browser?.servers) return [];
    return Object.entries(browser.servers).map(([key, credential]) => {
        const url = `${key}.openeyes.com`;
        return {
            urls: [`stun:${url}:3478`, `turn:${url}:3478`],
            username: browser.username,
            credential,
        };
    });
};
